import type { Cache } from "swr";
import { SWRGlobalState } from "swr/_internal";

import { isProtectedClientPortalCacheKey } from "../providers/clientPortalCacheAllowlist";

type BoundMutate = {
  (
    key: string,
    data?: undefined,
    options?: { revalidate: boolean; populateCache?: boolean }
  ): Promise<unknown>;
  (
    filter: (key: string) => boolean,
    data?: undefined,
    options?: { revalidate: boolean; populateCache?: boolean }
  ): Promise<unknown>;
};

const PURGE_MUTATE_OPTIONS = {
  revalidate: false,
  populateCache: false,
} as const;

export function deleteProtectedCacheEntries(cache: Cache): string[] {
  const deleted: string[] = [];

  for (const key of cache.keys()) {
    if (isProtectedClientPortalCacheKey(key)) {
      cache.delete(key);
      deleted.push(key);
    }
  }

  return deleted;
}

// A purge with `revalidate: false` leaves SWR's dedupe marker behind (kept while
// the request is in flight and for `dedupingInterval` after it resolves). A hook
// that remounts in that window joins the old request, and SWR then discards its
// response as older than the purge, leaving the hook with no data, no error and
// no retry.
function clearProtectedInFlightRequests(cache: Cache): void {
  const inFlightRequests = SWRGlobalState.get(cache)?.[2];
  if (!inFlightRequests) {
    return;
  }

  for (const key of Object.keys(inFlightRequests)) {
    if (isProtectedClientPortalCacheKey(key)) {
      delete inFlightRequests[key];
    }
  }
}

export async function purgeProtectedSwrCache(
  cache: Cache,
  mutate: BoundMutate,
  inFlightKeys: string[] = []
): Promise<string[]> {
  const candidateKeys = new Set<string>([
    ...cache.keys(),
    ...inFlightKeys.filter((key) => isProtectedClientPortalCacheKey(key)),
  ]);

  await mutate((key) => isProtectedClientPortalCacheKey(key), undefined, {
    ...PURGE_MUTATE_OPTIONS,
  });

  for (const key of candidateKeys) {
    if (isProtectedClientPortalCacheKey(key)) {
      await mutate(key, undefined, PURGE_MUTATE_OPTIONS);
    }
  }

  const deleted = deleteProtectedCacheEntries(cache);

  await Promise.resolve();

  await mutate((key) => isProtectedClientPortalCacheKey(key), undefined, {
    ...PURGE_MUTATE_OPTIONS,
  });

  for (const key of cache.keys()) {
    if (isProtectedClientPortalCacheKey(key)) {
      cache.delete(key);
      if (!deleted.includes(key)) {
        deleted.push(key);
      }
    }
  }

  clearProtectedInFlightRequests(cache);

  return deleted;
}
